import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { LoginForm } from '../components/LoginForm';
import { OTPForm } from '../components/OTPForm';
import { SignupForm } from '../components/SignupForm';
import { TwoFactorSetup } from '../components/TwoFactorSetup';
import { Navigate, useLocation } from 'react-router-dom';

type AuthMode = 'login' | 'signup' | 'otp';

export const AuthPage: React.FC = () => {
  const location = useLocation();
  const [mode, setMode] = useState<AuthMode>('login');
  const { 
    user, 
    requires2FA, 
    setRequires2FA, 
    needsTwoFactorSetup, 
    setNeedsTwoFactorSetup,
    signupUserEmail 
  } = useAuth();

  // Set mode based on current path
  useEffect(() => {
    if (location.pathname === '/signup') {
      setMode('signup');
    } else {
      setMode('login');
    }
  }, [location.pathname]);

  // Redirect to dashboard if already logged in
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show 2FA setup after successful signup
  if (needsTwoFactorSetup && signupUserEmail) {
    return (
      <TwoFactorSetup
        userEmail={signupUserEmail}
        onSetupComplete={() => {
          setNeedsTwoFactorSetup(false);
          setMode('login');
          // Show success message that they can now login
        }}
        onCancel={() => {
          setNeedsTwoFactorSetup(false);
          setMode('signup');
        }}
      />
    );
  }

  // Show OTP form if 2FA is required
  if (requires2FA && mode === 'login') {
    const tempUsername = localStorage.getItem('temp_username');
    const tempPassword = localStorage.getItem('temp_password');
    
    if (tempUsername && tempPassword) {
      return (
        <OTPForm
          username={tempUsername}
          password={tempPassword}
          onBack={() => {
            setRequires2FA(false);
            localStorage.removeItem('temp_username');
            localStorage.removeItem('temp_password');
          }}
        />
      );
    }
  }

  const handleSwitchToSignup = () => {
    setMode('signup');
    setRequires2FA(false);
    setNeedsTwoFactorSetup(false);
  };

  const handleSwitchToLogin = () => {
    setMode('login');
    setRequires2FA(false);
    setNeedsTwoFactorSetup(false);
  };

  if (mode === 'signup') {
    return <SignupForm onSwitchToLogin={handleSwitchToLogin} />;
  }

  return <LoginForm onSwitchToSignup={handleSwitchToSignup} />;
};